import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "../../lib/http.ts";

interface Template {
  id: string;
  name: string;
  template: string;
  isDefault: boolean;
  active: boolean;
}

export function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Template | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTemplate, setNewTemplate] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiRequest<{ items: Template[] }>("/api/v1/templates");
      setTemplates(result.items ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generatePreview = (template: string) => {
    const sample = template
      .replace(/\{\{title\}\}/g, "iPhone 15 Pro Max")
      .replace(/\{\{currentPrice\}\}/g, "7499.90")
      .replace(/\{\{previousPrice\}\}/g, "8999.90")
      .replace(/\{\{discount\}\}/g, "17")
      .replace(/\{\{coupon\}\}/g, "IPHONE10")
      .replace(/\{\{link\}\}/g, "https://www.amazon.com.br/dp/B08N5WRWNW");
    setPreview(sample);
  };

  const saveTemplate = async () => {
    if (!editing) return;
    setError(null);
    try {
      await apiRequest(`/api/v1/templates/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify(editing),
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar template.");
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
        >
          {creating ? "Cancelar" : "Novo template"}
        </button>
      </div>

      {creating && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-3 font-medium">Novo template</h3>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Nome</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Template</label>
              <textarea
                value={newTemplate}
                onChange={(e) => setNewTemplate(e.target.value)}
                rows={10}
                className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
                required
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Variáveis: {"{{title}}"}, {"{{currentPrice}}"}, {"{{previousPrice}}"},{" "}
                {"{{discount}}"}, {"{{coupon}}"}, {"{{link}}"}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={saving || !newName.trim() || !newTemplate.trim()}
                onClick={async () => {
                  setSaving(true);
                  setError(null);
                  try {
                    await apiRequest("/api/v1/templates", {
                      method: "POST",
                      body: JSON.stringify({ name: newName.trim(), template: newTemplate.trim() }),
                    });
                    setCreating(false);
                    setNewName("");
                    setNewTemplate("");
                    await load();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Erro ao criar template.");
                  } finally {
                    setSaving(false);
                  }
                }}
                className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
              >
                {saving ? "Salvando..." : "Criar"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setNewName("");
                  setNewTemplate("");
                }}
                className="rounded-md border px-3 py-2 text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {editing
        ? (
          <div className="rounded-lg border bg-card p-4">
            <h3 className="mb-3 font-medium">Editando: {editing.name}</h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Nome</label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Template</label>
                <textarea
                  value={editing.template}
                  onChange={(e) => setEditing({ ...editing, template: e.target.value })}
                  rows={10}
                  className="w-full rounded-md border bg-background px-3 py-2 font-mono text-sm"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Variáveis: {"{{title}}"}, {"{{currentPrice}}"}, {"{{previousPrice}}"},{" "}
                  {"{{discount}}"}, {"{{coupon}}"}, {"{{link}}"}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Preview</label>
                <pre className="whitespace-pre-wrap rounded-md border bg-muted p-3 text-sm">
                {generatePreview(editing.template) || editing.template}
                </pre>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={saveTemplate}
                  className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
                >
                  Salvar
                </button>
                <button
                  onClick={() => setEditing(null)}
                  className="rounded-md border px-3 py-2 text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )
        : loading
        ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-lg bg-muted"
              />
            ))}
          </div>
        )
        : templates.length === 0
        ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Nenhum template configurado.
          </div>
        )
        : (
          <div className="space-y-3">
            {templates.map((tpl) => (
              <div key={tpl.id} className="rounded-lg border bg-card p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{tpl.name}</h4>
                    <div className="mt-1 flex gap-2">
                      {tpl.isDefault && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                          Padrão
                        </span>
                      )}
                      {!tpl.active && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          Inativo
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setEditing(tpl)}
                    className="rounded-md border px-3 py-1.5 text-sm"
                  >
                    Editar
                  </button>
                </div>
                <pre className="mt-3 whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                {tpl.template}
                </pre>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
